/**
 * Définition du parcours d'onboarding et logique pure d'évaluation d'une étape.
 *
 * Aucun accès DOM ni React ici : `OnboardingTourGate` observe la page et passe
 * ses observations à `evaluateStep`, ce qui rend la progression testable sans
 * navigateur.
 */

export type TourStepId =
  | 'create-case'
  | 'open-case'
  | 'dossier-sections'
  | 'add-pieces'
  | 'rename-pieces'
  | 'split-piece'
  | 'other-sections'
  | 'stamp-pieces'
  | 'stamp-flow'
  | 'ask-hector'
  | 'hector-actions'
  | 'dossier-modes'
  | 'new-conversation'
  | 'select-case'
  | 'first-message'
  | 'drafting-flow'
  | 'conversations-list'
  | 'help-link'
  | 'support-chat';

/**
 * - `next` : étape d'information, l'utilisateur clique « Suivant ».
 * - `anchor-click` : l'utilisateur doit cliquer l'élément surligné (qui ouvre
 *   en général une fenêtre modale ; le parcours se met alors en retrait).
 * - `route` : l'étape est franchie quand l'URL correspond à `doneWhen`.
 * - `element` : l'étape est franchie quand `doneWhenVisible` apparaît à l'écran.
 * - `anchor-gone` : la bulle commente une fenêtre où l'utilisateur doit
 *   répondre ; l'étape est franchie quand cette fenêtre se referme. Pas de
 *   bouton « Suivant ».
 */
export type TourAdvance = 'next' | 'anchor-click' | 'route' | 'element' | 'anchor-gone';

export interface TourStep {
  id: TourStepId;
  /** Chemins (pathname) sur lesquels l'étape peut s'afficher. */
  route: RegExp;
  /** Sélecteur CSS de l'élément surligné. Absent : bulle centrée à l'écran. */
  anchor?: string;
  title: string;
  description: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  advance: TourAdvance;
  /** Testé sur `pathname + search`. Obligatoire quand `advance === 'route'`. */
  doneWhen?: RegExp;
  /** Sélecteur CSS dont l'apparition franchit l'étape. Obligatoire quand `advance === 'element'`. */
  doneWhenVisible?: string;
  /** Sur une étape sans bouton « Suivant », ajoute un bouton « Passer ». */
  skippable?: boolean;
  /** Ancre durablement introuvable : passer à l'étape suivante plutôt que rester bloqué. */
  skipIfMissing?: boolean;
  /**
   * L'ancre vit DANS une fenêtre modale (panneau de tamponnage) : l'étape
   * s'affiche alors par-dessus au lieu de se retirer.
   */
  insideModal?: boolean;
  /** Sur une étape `next`, laisse l'élément surligné cliquable. */
  interactive?: boolean;
  /** Bulle plus large et en plus gros caractères, pour un texte à lire posément. */
  large?: boolean;
  /**
   * Étape antérieure vers laquelle revenir quand l'ancre reste introuvable
   * (ex. l'utilisateur a fermé la fenêtre d'import sans déposer de pièce).
   */
  fallback?: TourStepId;
  /**
   * L'ancre n'existe que dans une partie de la page du dossier : l'onglet
   * « Mes pièces » (`pieces`) ou le mode « Gestion des pièces », tous onglets
   * confondus (`any`). Si l'utilisateur est ailleurs dans le dossier, une bulle
   * l'invite à y revenir au lieu de laisser le parcours disparaître sans un mot.
   */
  dossierTab?: 'pieces' | 'any';
}

/** Présent seulement dans l'onglet « Mes pièces » : prouve qu'il est affiché. */
export const PIECES_TAB_MARKER = '[data-tour="add-pieces"]';
/** Rangée d'onglets du mode « Gestion des pièces ». */
export const DOSSIER_TABS = '[data-tour="dossier-sections"]';
/** Segment « Gestion des pièces » de la bascule de mode, visible dans les deux modes. */
export const GESTION_MODE = '[data-tour="dossier-mode-gestion"]';

/**
 * Onglets cités par une bulle et dont le libellé dépend de la matière du
 * dossier (« Mes écritures » devient « Mes mémoires » en droit public, « Partie
 * adverse » devient « Productions du défendeur »). La clé est la valeur de
 * l'onglet dans `DossierView`, le libellé est celui d'un dossier civil : il
 * sert quand l'onglet n'est pas à l'écran (sélecteur déroulant sur mobile).
 */
export const TAB_LABEL_FALLBACKS = {
  procedure: 'Mes écritures',
  'partie-adverse': 'Partie adverse',
} as const;
export type TourTabKey = keyof typeof TAB_LABEL_FALLBACKS;

/** Attribut posé sur chaque onglet du dossier ; sa valeur est celle de l'onglet. */
export const TAB_LABEL_ATTRIBUTE = 'data-tour-tab';
/** Sélecteur des onglets porteurs de l'attribut, pour le garde-fou des ancres. */
export const TAB_LABEL_MARKER = `[${TAB_LABEL_ATTRIBUTE}]`;

/**
 * Remplace chaque `{{onglet:<clé>}}` d'un texte de bulle par le libellé que
 * l'avocat lit à l'écran. Une bulle qui nommerait « Mes écritures » dans un
 * dossier de droit public enverrait l'utilisateur chercher un onglet absent.
 *
 * @param readLabel rend le libellé affiché de l'onglet, ou `null` s'il n'est pas à l'écran.
 */
export function fillTabLabels(text: string, readLabel: (tab: TourTabKey) => string | null): string {
  return text.replace(/\{\{onglet:([a-z-]+)\}\}/g, (placeholder, key: string) => {
    if (!(key in TAB_LABEL_FALLBACKS)) return placeholder;
    const tab = key as TourTabKey;
    return readLabel(tab) ?? TAB_LABEL_FALLBACKS[tab];
  });
}

/** Bulles de rappel affichées quand l'utilisateur a quitté l'écran de l'étape. */
export const REDIRECT_BUBBLES = {
  pieces: {
    title: 'La suite est dans vos pièces',
    description:
      'Le tutoriel continue dans le premier onglet du dossier, celui de vos pièces. Cliquez dessus pour reprendre.',
  },
  gestion: {
    title: 'Revenez dans « Gestion des pièces »',
    description: 'Le tutoriel continue dans « Gestion des pièces ». Cliquez dessus pour reprendre.',
  },
} as const;

const DASHBOARD = /^\/$/;
const CASE_PAGE = /^\/cases\/[^/]+\/?$/;
/**
 * Même page, testée sur `pathname + search` : l'onglet et le mode du dossier
 * vivent dans l'URL (`?tab=`, `?mode=`, `?section=`), l'arrivée sur un dossier
 * ne se fait donc pas toujours sur une URL nue.
 */
const CASE_URL = /^\/cases\/[^/?]+\/?(\?|$)/;
const ASSISTANT = /^\/assistant\/?$/;
/** Barre latérale et chat de support : présents sur tous les écrans. */
const ANYWHERE = /^\//;

export const TOUR_STEPS: readonly TourStep[] = [
  {
    id: 'create-case',
    route: DASHBOARD,
    anchor: '[data-tour="new-case"]',
    title: 'Commencez par créer un dossier',
    description:
      'Un dossier regroupe vos pièces, vos écritures et vos échanges avec Hector. Cliquez sur « Nouveau dossier », puis renseignez son nom, le client et le type de procédure.',
    side: 'bottom',
    // Franchie à la création effective, pas au clic : `CaseFormDialog` pose cet
    // attribut une fois le dossier créé. Une fenêtre fermée sans créer
    // ré-affiche donc simplement cette bulle.
    advance: 'element',
    doneWhenVisible: '[data-tour="new-case"][data-tour-case-created]',
  },
  {
    id: 'open-case',
    route: DASHBOARD,
    // Toute la liste, pas une ligne : le tri choisi par l'utilisateur décide
    // de l'endroit où son dossier apparaît.
    anchor: '[data-tour="case-list"]',
    title: 'Ouvrez votre dossier',
    description:
      'Votre dossier est créé : il apparaît dans cette liste, avec son client, sa procédure et son échéance. Cliquez sur sa ligne pour l’ouvrir.',
    side: 'top',
    advance: 'route',
    doneWhen: CASE_URL,
  },
  {
    id: 'dossier-sections',
    route: CASE_PAGE,
    anchor: '[data-tour="dossier-sections"]',
    title: 'Votre dossier est prêt',
    description:
      'Chaque onglet range un type de document : vos pièces, vos écritures, celles de l’adversaire, ce qui vient de la juridiction et du greffe, vos recherches… Vous commencez dans « Mes pièces ».',
    side: 'bottom',
    advance: 'next',
    dossierTab: 'any',
  },
  {
    id: 'add-pieces',
    route: CASE_PAGE,
    anchor: '[data-tour="add-pieces"]',
    title: 'Déposez vos pièces',
    description:
      'Cliquez sur « Ajouter des pièces » pour importer des fichiers ou un dossier complet. Hector lit ensuite chaque pièce en arrière-plan.',
    side: 'bottom',
    advance: 'anchor-click',
    dossierTab: 'pieces',
  },
  {
    id: 'rename-pieces',
    route: CASE_PAGE,
    anchor: '[data-tour="rename-pieces"]',
    insideModal: true,
    title: 'Renommage automatique',
    description:
      'Hector peut renommer chaque pièce à votre place, d’après son contenu et selon les conventions des avocats : nature du document, auteur, date. Répondez « Oui, renommer » pour le laisser faire, ou « Non » pour garder vos noms de fichiers.',
    side: 'bottom',
    advance: 'anchor-gone',
    // Les boutons « Oui » / « Non » sont dans la fenêtre surlignée.
    interactive: true,
    // Import annulé, ou dépôt par glisser-déposer sans cette question.
    skipIfMissing: true,
  },
  {
    id: 'split-piece',
    route: CASE_PAGE,
    anchor: '[data-tour="piece-actions"]',
    title: 'Un PDF, plusieurs pièces ?',
    description:
      'Ouvrez ce menu : « Scinder ou réorganiser les pages » découpe un PDF en plusieurs pièces que vous nommez une à une. Vous y trouvez aussi renommer, déplacer et tamponner.',
    side: 'left',
    advance: 'anchor-click',
    skippable: true,
    fallback: 'add-pieces',
    dossierTab: 'pieces',
  },
  {
    id: 'other-sections',
    route: CASE_PAGE,
    anchor: DOSSIER_TABS,
    title: 'En défense ou en réplique ?',
    // « Une fois ce tutoriel terminé » : l'étape suivante est ancrée dans
    // l'onglet des pièces. Une consigne à l'impératif immédiat envoyait
    // l'utilisateur dans un autre onglet, où la bulle de rappel le ramenait
    // aussitôt sans le laisser déposer quoi que ce soit.
    description:
      'Hector a alors besoin de plus que vos pièces. Une fois ce tutoriel terminé, déposez les écritures et les pièces de l’adversaire dans « {{onglet:partie-adverse}} », et vos propres écritures dans « {{onglet:procedure}} » : il s’appuie dessus pour répondre.',
    side: 'bottom',
    advance: 'next',
    dossierTab: 'any',
  },
  {
    id: 'stamp-pieces',
    route: CASE_PAGE,
    anchor: '[data-tour="stamp-pieces"]',
    title: 'Tamponnez vos pièces',
    description:
      'Avant de communiquer vos pièces, numérotez-les et apposez votre tampon. Cliquez sur « Tamponner mes pièces ».',
    side: 'bottom',
    advance: 'anchor-click',
    dossierTab: 'pieces',
  },
  {
    id: 'stamp-flow',
    route: CASE_PAGE,
    anchor: '[data-tour="stamp-steps"]',
    insideModal: true,
    title: 'Le tamponnage en trois étapes',
    description:
      '1. Pièces : numérotez-les, à la main ou avec l’IA.<br>2. Tampon : choisissez ou ajoutez le vôtre.<br>3. Export : pièces tamponnées, avec ou sans bordereau.<br>Explorez le panneau, puis fermez-le avec la croix pour continuer.',
    side: 'bottom',
    advance: 'next',
    // La croix du panneau est dans l'en-tête surligné : elle doit rester cliquable.
    interactive: true,
    // Panneau non ouvert (aucune pièce à tamponner) ou refermé aussitôt.
    skipIfMissing: true,
  },
  {
    id: 'ask-hector',
    route: CASE_PAGE,
    anchor: '[data-tour="ask-hector"]',
    title: 'Demander à Hector',
    description:
      'Hector peut ranger vos pièces à votre place. Cliquez sur « Demander à Hector » pour ouvrir l’assistant du dossier.',
    side: 'left',
    advance: 'anchor-click',
  },
  {
    id: 'hector-actions',
    route: CASE_PAGE,
    anchor: '[data-testid="embedded-quick-actions"]',
    title: 'Ce que vous pouvez lui demander',
    description:
      'Organiser vos pièces en sous-dossiers, les renommer, les renuméroter, ou générer un bordereau à partir d’un acte. Vous choisissez une action ou écrivez votre demande : Hector propose, vous confirmez.',
    side: 'left',
    advance: 'next',
    skipIfMissing: true,
  },
  {
    id: 'dossier-modes',
    route: CASE_PAGE,
    // Le seul segment « Analyse avancée », pas toute la bascule : celle-ci
    // porte aussi « Gestion financière », que le parcours ne présente pas.
    anchor: '[data-tour="dossier-mode-analyse"]',
    title: 'Analyse avancée',
    description:
      '« Analyse avancée » fait lire tout le dossier à Hector pour en tirer la chronologie, les parties et les points de procédure. Elle tourne automatiquement en arrière-plan : ses réponses gagnent en fiabilité et en rapidité, et vous continuez à travailler pendant ce temps.',
    side: 'bottom',
    advance: 'next',
  },
  {
    id: 'new-conversation',
    route: CASE_PAGE,
    anchor: '[data-tour="new-conversation"]',
    title: 'Rédigez un acte avec Hector',
    description:
      'Pour produire un acte, une recherche ou une analyse, démarrez une conversation avec Hector : cliquez sur le bouton mis en évidence.',
    side: 'right',
    advance: 'route',
    doneWhen: /^\/assistant/,
  },
  {
    id: 'select-case',
    route: ASSISTANT,
    anchor: '[data-tour="assistant-case-selector"]',
    title: 'Indiquez votre dossier',
    description:
      'Cliquez sur « Sélectionner un dossier » et choisissez le vôtre : Hector s’appuiera sur ses pièces et leurs synthèses. Sans dossier, il répond de façon générale.',
    side: 'top',
    advance: 'element',
    doneWhenVisible: '[data-tour="assistant-case-selected"]',
    // Le sélecteur est masqué quand la conversation est verrouillée sur un dossier.
    skipIfMissing: true,
  },
  {
    id: 'first-message',
    route: ASSISTANT,
    anchor: '[data-tour="assistant-composer"]',
    title: 'Envoyez votre demande',
    description:
      'Écrivez ce que vous attendez, par exemple « Rédige une assignation », « Rédige des conclusions en défense » ou « Rédige des conclusions en réplique », puis envoyez.',
    side: 'top',
    advance: 'route',
    // Une conversation n'existe qu'à l'envoi du premier message : l'URL passe
    // alors à `/assistant?c=<id>`.
    doneWhen: /^\/assistant\/?\?(.*&)?c=/,
  },
  {
    id: 'drafting-flow',
    route: ASSISTANT,
    // Sans ancre : la bulle se centre sur la page.
    large: true,
    title: 'Ce qui se passe maintenant',
    description:
      '1. Hector étudie le dossier et vous propose une stratégie : comptez quelques minutes.<br>2. Vous répondez à ses questions, puis validez avec « Lancer ».<br>3. Il construit le plan détaillé de votre « II. Discussion juridique », dans le document à droite.<br>4. Vous relisez le plan, le modifiez ou en discutez.<br>5. Vous lancez la rédaction de l’acte complet.',
    advance: 'next',
  },
  {
    id: 'conversations-list',
    route: ANYWHERE,
    anchor: '[data-tour="conversations-list"]',
    title: 'Vos conversations',
    description:
      'Chaque échange avec Hector est conservé ici, rattaché à son dossier. Reprenez une conversation à tout moment, renommez-la ou épinglez-la pour la retrouver en tête de liste.',
    side: 'right',
    advance: 'next',
    // La liste est masquée quand la barre latérale est repliée.
    skipIfMissing: true,
  },
  {
    id: 'help-link',
    route: ANYWHERE,
    anchor: '[data-tour="help-link"]',
    title: 'Aide et formation',
    description:
      'Des tutoriels vidéo présentent chaque fonctionnalité, de la prise en main à la rédaction d’actes. Vous pouvez aussi y relancer ce tutoriel quand vous le souhaitez.',
    side: 'right',
    advance: 'next',
    // En fenêtre étroite, l'Aide est rangée dans le menu Profil, fermé : sans
    // ce saut, le parcours attendait une ancre invisible et ne finissait jamais.
    skipIfMissing: true,
  },
  {
    id: 'support-chat',
    route: ANYWHERE,
    // Widget Crisp, injecté par un script tiers : aucun `data-tour` possible.
    anchor: '#crisp-chatbox [role="button"]',
    title: 'Une question ? Écrivez-nous',
    description:
      'Ce chat vous met en relation avec l’équipe Hector, pour un blocage ou une suggestion. Vos questions juridiques, posez-les à Hector dans une conversation.',
    side: 'left',
    advance: 'next',
    // Widget absent si son script est bloqué (bloqueur de publicités), et
    // masqué en fenêtre étroite (`hooks/useCrisp.ts`).
    skipIfMissing: true,
  },
];

export const FIRST_STEP_ID: TourStepId = 'create-case';

export function getStepIndex(id: TourStepId): number {
  return TOUR_STEPS.findIndex((step) => step.id === id);
}

export function getStep(id: TourStepId): TourStep | undefined {
  return TOUR_STEPS[getStepIndex(id)];
}

/** Étape suivante, ou `null` quand `id` est la dernière. */
export function getNextStepId(id: TourStepId): TourStepId | null {
  return TOUR_STEPS[getStepIndex(id) + 1]?.id ?? null;
}

export function isTourStepId(value: unknown): value is TourStepId {
  return typeof value === 'string' && TOUR_STEPS.some((step) => step.id === value);
}

/** Ce que le moteur a observé sur la page à un instant donné. */
export interface StepObservation {
  pathname: string;
  /** `pathname + search`. */
  location: string;
  /** Une fenêtre modale (dialogue, panneau latéral, popover) est ouverte. */
  modalOpen: boolean;
  /** L'ancre de l'étape est présente et visible. */
  anchorVisible: boolean;
  /** Le sélecteur `doneWhenVisible` de l'étape est présent et visible. */
  doneVisible: boolean;
  /** L'écran exigé par `step.dossierTab` est affiché. Sans objet sinon. */
  screenVisible?: boolean;
  /** Un élément permet de revenir à cet écran (onglets ou bascule de mode). */
  redirectVisible?: boolean;
}

/**
 * - `advance` : l'étape est franchie, passer à la suivante.
 * - `show` : afficher la bulle.
 * - `wait` : ne rien afficher, la situation est transitoire ou hors sujet
 *   (autre page, fenêtre modale ouverte).
 * - `missing` : on est sur la bonne page, rien ne masque l'écran, mais l'ancre
 *   est absente. Si cela dure, le moteur revient à `step.fallback`.
 * - `redirect` : l'utilisateur a quitté l'onglet ou le mode du dossier où vit
 *   l'ancre ; afficher la bulle qui l'y ramène.
 */
export type StepVerdict = 'advance' | 'show' | 'wait' | 'missing' | 'redirect';

export function evaluateStep(step: TourStep, observation: StepObservation): StepVerdict {
  if (step.advance === 'route' && step.doneWhen?.test(observation.location)) {
    return 'advance';
  }
  if (step.advance === 'element' && step.route.test(observation.pathname) && observation.doneVisible) {
    return 'advance';
  }
  if (!step.route.test(observation.pathname)) return 'wait';
  // Une étape ordinaire se retire derrière toute fenêtre. Une étape `insideModal`
  // attend SA fenêtre : tant qu'une autre est ouverte (le formulaire d'import
  // avant la question de renommage), on patiente au lieu de la déclarer manquante.
  if (observation.modalOpen && (!step.insideModal || !observation.anchorVisible)) return 'wait';
  if (step.anchor === undefined || observation.anchorVisible) return 'show';
  // Ancre absente parce que l'utilisateur est ailleurs dans le dossier : ce
  // n'est pas une ancre « manquante » (pas de retour arrière), on le ramène.
  if (step.dossierTab && observation.screenVisible === false) {
    return observation.redirectVisible ? 'redirect' : 'wait';
  }
  return 'missing';
}
